import {
  isPropertyScopedProductSet,
  offerProductSet,
  offerPropertyAddress,
  offerPropertyKey
} from '@ourbranch/offer-utils';

/**
 * A fresh quote bridged in from a comparative rater: created by the rater's first rate call
 * (fromRC1), not started in Staff, never saved over (no parentId).
 *
 * Gates comp-rater merge eligibility (offer-store).
 */
export const isComparativeRaterQuote = (offer) =>
  Boolean(offer?.quote?.global?.fromRC1) && !offer?.quote?.fromStaff && !offer?.parentId;

export const isPartnerOriginatedQuote = (offer) =>
  Boolean(offer?.quote?.global?.fromRC1 || offer?.quote?.global?.fromApi) &&
  !offer?.quote?.fromStaff &&
  !offer?.parentId;

/**
 * Which offer is the "canonical" one staff get auto-redirected to (#30994, #35043).
 *
 * A fresh partner-created quote (comp-rater rate call or full API quote) always yields to an offer that HAS been saved
 * over (carries the prior offer's id as parentId), regardless of which is newer: the agent requoted
 * in their rater for reasons unrelated to Branch and must not lose their saved Branch edits. Every
 * other offer keeps the original rule — the candidate must be strictly newer by updatedDateTime —
 * so a quote deliberately started in Staff (past the "we found N offers for this person" warning or
 * via "generate from scratch") is never overridden by an older edit, and Meilisearch index lag (the
 * offer being viewed not indexed yet, so an older sibling tops the page) can't bounce staff
 * backwards.
 */
export const shouldRedirectToOffer = ({ currentOffer, candidate }) => {
  if (!candidate?.id || candidate.id === currentOffer.id || !candidate.updatedDateTime) {
    return false;
  }

  if (candidate.parentId && isPartnerOriginatedQuote(currentOffer)) {
    return true;
  }

  // A missing timestamp on the current offer counts as "candidate wins" so malformed/legacy data
  // doesn't silently suppress the redirect
  return (
    !currentOffer.updatedDateTime ||
    new Date(candidate.updatedDateTime).getTime() > new Date(currentOffer.updatedDateTime).getTime()
  );
};

const REDIRECT_PROPERTY_FIELDS = ['address', 'address2', 'unit', 'city', 'state', 'zip'];

/**
 * The property the redirect must stay on (#36618), or null when it doesn't apply. A mutual member can
 * hold several quotes of the same farm-system type (F/Z/Y) at different properties, which share a
 * webUserId and a product set, so the property tells them apart. `address` is the
 * AddressDetailsInput the backend fallback takes (only the fields the property key reads); `key` is
 * what candidates are compared on.
 */
export const redirectPropertyOf = (offer) => {
  if (!isPropertyScopedProductSet(offerProductSet(offer?.options))) {
    return null;
  }
  const property = offerPropertyAddress(offer);
  const key = offerPropertyKey(property);
  if (!key) {
    return null;
  }
  const address = Object.fromEntries(REDIRECT_PROPERTY_FIELDS.map((field) => [field, property[field] ?? null]));
  return { key, address };
};
